/**
 * Act2BottomPanel — 第二幕底部面板
 *
 * LLM 生成卡牌手牌 + 静观其变
 */
import React, { useState } from 'react';
import { Eye, Sparkles, AlertTriangle, Flame } from 'lucide-react';
import type { Act2Card } from '@shared/types/cards';
import { ACT2_HAND_SIZE } from '@shared/constants/gameBalance';

interface Act2BottomPanelProps {
  hand: Act2Card[];
  deckSize: number;
  phase: string;
  swapCount: number;
  maxSwaps: number;
  onPlayCard: (card: Act2Card) => void;
  onSkip: () => void;
}

export const Act2BottomPanel: React.FC<Act2BottomPanelProps> = React.memo(({
  hand,
  deckSize,
  phase,
  swapCount,
  maxSwaps,
  onPlayCard,
  onSkip,
}) => {
  const isPlayerTurn = phase === 'PLAYER_TURN';

  return (
    <div className="bg-white/95 backdrop-blur-md border-t border-[#E5E0D8] p-3 shrink-0 safe-area-bottom shadow-lg">
      {/* Deck info */}
      <div className="flex justify-end items-center mb-2 px-1">
        <div className="flex items-center gap-2 text-[10px] text-[#6B7280]">
          <span className="px-2 py-0.5 bg-[#F5EDE3] rounded-full font-medium">
            牌堆 {deckSize}
          </span>
          <span className="px-2 py-0.5 bg-[#F5EDE3] rounded-full font-medium">
            手牌 {hand.length}/{ACT2_HAND_SIZE}
          </span>
          <span className="px-2 py-0.5 bg-[#F5EDE3] rounded-full font-medium">
            换牌 {swapCount}/{maxSwaps}
          </span>
        </div>
      </div>

      {/* Hand cards */}
      <div className="relative">
        <div className="absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-l from-white/90 to-transparent z-10 pointer-events-none sm:hidden" />
        <div className="flex items-stretch gap-2 px-2 overflow-x-auto pb-1 sm:justify-center">
          {/* Skip button */}
          <button
            disabled={!isPlayerTurn}
            onClick={onSkip}
            className={`
              relative w-[70px] sm:w-[90px] aspect-[2/3] flex-shrink-0
              bg-gradient-to-br from-[#F5EDE3] via-[#FAF6F1] to-[#EDE8F5]
              border-2 border-[#D4C8B8] rounded-lg shadow-md overflow-hidden
              flex flex-col items-center justify-center gap-1
              transition-all duration-300 ease-out
              ${!isPlayerTurn ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-lg hover:-translate-y-1 cursor-pointer active:scale-95'}
            `}
          >
            <Eye className="w-5 h-5 text-[#9B8EC4]" />
            <span className="text-[10px] font-bold text-[#6B5B4F]">静观其变</span>
          </button>

          {/* Card list */}
          {hand.map(card => (
            <Act2HandCard
              key={card.id}
              card={card}
              isClickable={isPlayerTurn}
              onClick={() => onPlayCard(card)}
            />
          ))}
        </div>
      </div>
    </div>
  );
});

Act2BottomPanel.displayName = 'Act2BottomPanel';

// ── 单张手牌 ──────────────────────────────────

interface Act2HandCardProps {
  card: Act2Card;
  isClickable: boolean;
  onClick: () => void;
}

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  '撮合': <Sparkles size={10} />,
  '拆散': <AlertTriangle size={10} />,
  '整活': <Flame size={10} />,
};

const RISK_COLORS: Record<string, { border: string; bg: string }> = {
  safe: { border: 'border-green-300', bg: 'bg-green-50' },
  normal: { border: 'border-amber-300', bg: 'bg-amber-50' },
  risky: { border: 'border-red-300', bg: 'bg-red-50' },
};

const TYPE_COLORS: Record<string, string> = {
  '话题引导': 'text-blue-600 bg-blue-50 border-blue-200',
  '情感催化': 'text-pink-600 bg-pink-50 border-pink-200',
  '场景事件': 'text-amber-600 bg-amber-50 border-amber-200',
};

const Act2HandCard: React.FC<Act2HandCardProps> = ({ card, isClickable, onClick }) => {
  const risk = RISK_COLORS[card.riskLevel] || RISK_COLORS.normal;
  const typeStyle = TYPE_COLORS[card.type] || 'text-gray-600 bg-gray-50 border-gray-200';

  return (
    <button
      disabled={!isClickable}
      onClick={onClick}
      className={`
        relative w-[70px] sm:w-[90px] aspect-[2/3] flex-shrink-0
        ${risk.bg} border-2 ${risk.border}
        rounded-lg shadow-md overflow-hidden flex flex-col
        transition-all duration-300 ease-out
        ${!isClickable ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-lg hover:-translate-y-1 cursor-pointer active:scale-95'}
      `}
    >
      {/* Type + category badges */}
      <div className="p-1 flex items-center justify-between">
        <span className={`text-[7px] px-1 py-0.5 rounded-full font-medium border ${typeStyle}`}>
          {card.type}
        </span>
        <span className="text-[#9CA3AF]">{CATEGORY_ICONS[card.category]}</span>
      </div>

      {/* Card name */}
      <div className="flex-1 flex items-center justify-center px-1">
        <span className="text-[11px] sm:text-xs font-bold text-[#2D2D2D] text-center leading-tight">
          {card.name}
        </span>
      </div>

      {/* Hint */}
      <div className="p-1">
        <span className="text-[7px] sm:text-[8px] text-[#9CA3AF] text-center block truncate">
          {card.hint}
        </span>
      </div>
    </button>
  );
};
